import React, { useState, useEffect } from 'react';
import { MdDownload, MdReceipt, MdLocalShipping } from 'react-icons/md';
import api from '../services/api';
import { getCurrentUser } from '../services/authService';
import ClientLayout from '../components/ClientLayout';

const BRAND = '#068BC9';

export default function ClientCompanyInvoices() {
  const user = getCurrentUser();
  const [activeTab, setActiveTab] = useState('invoices');
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);

  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [exportLoading, setExportLoading] = useState(false);

  const selectedCity = localStorage.getItem('selectedCity') || 'All Cities';
  const companyCity = localStorage.getItem('companyCity') || '';
  const companyName = localStorage.getItem('companyName') || 'Your company';
  const cityBlocked = selectedCity !== 'All Cities' && companyCity && selectedCity !== companyCity;

  useEffect(() => {
    if (!user?.companyId) return;
    api.get(`/api/company-invoices/company/${user.companyId}`)
      .then(res => setInvoices(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [user?.companyId]);

  const handleShipmentExport = async () => {
    setExportLoading(true);
    try {
      const params = new URLSearchParams();
      if (user.companyId && user.companyId !== 'null') { params.append('companyId', user.companyId); }
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);

      const response = await api.get(`/api/company-invoices/shipment-export?${params.toString()}`, {
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'shipment-invoices.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert('Failed to export. Please try again.');
    } finally {
      setExportLoading(false);
    }
  };

  if (cityBlocked) return (
    <ClientLayout>
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-sm w-full mx-4 text-center">
          <div className="w-14 h-14 rounded-full bg-blue-50 flex items-center justify-center mx-auto mb-4">
            <svg width="28" height="28" fill="none" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" stroke="#068BC9" strokeWidth="2"/><path d="M12 8v4m0 4h.01" stroke="#068BC9" strokeWidth="2" strokeLinecap="round"/></svg>
          </div>
          <h2 className="text-lg font-bold text-gray-800 mb-2">{selectedCity} is not available</h2>
          <p className="text-sm text-gray-500 mb-6">{companyName} does not operate in {selectedCity}. Please select a valid city.</p>
          <button onClick={() => { localStorage.setItem('selectedCity', companyCity); window.history.back(); }}
            className="px-6 py-2 rounded-xl text-white text-sm font-medium" style={{ backgroundColor: '#068BC9' }}>
            Go Back
          </button>
        </div>
      </div>
    </ClientLayout>
  );

  return (
    <ClientLayout>
      <div className="p-6 space-y-6">
        <div>
          <h1 className="text-xl font-bold text-gray-800">Invoices</h1>
          <p className="text-xs text-gray-400">Monthly invoices from ProDiligix</p>
        </div>

        <div className="flex gap-1 border-b border-gray-200">
          <button
            onClick={() => setActiveTab('invoices')}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'invoices'
                ? 'border-[#068BC9] text-[#068BC9]'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Invoice History
          </button>
          <button
            onClick={() => setActiveTab('shipment')}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'shipment'
                ? 'border-[#068BC9] text-[#068BC9]'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Shipment Invoices
          </button>
        </div>

        {activeTab === 'invoices' && (
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-100">
              <p className="text-sm font-semibold text-gray-700">Invoice History ({invoices.length})</p>
            </div>
            {loading ? (
              <div className="p-8 text-center text-gray-400 text-sm">Loading...</div>
            ) : invoices.length === 0 ? (
              <div className="p-8 text-center">
                <MdReceipt size={36} className="text-gray-200 mx-auto mb-2" />
                <p className="text-sm text-gray-400">No invoices available yet</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>{['Invoice No.','Date','Service','Description','Amount','Action'].map(h => (
                      <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-500">{h}</th>
                    ))}</tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {invoices.map(inv => (
                      <tr key={inv.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-xs font-medium whitespace-nowrap" style={{color:BRAND}}>{inv.invoiceNumber}</td>
                        <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">{inv.invoiceDate}</td>
                        <td className="px-4 py-3 text-xs text-gray-600 whitespace-nowrap">{inv.serviceType}</td>
                        <td className="px-4 py-3 text-xs text-gray-500">{inv.description||'—'}</td>
                        <td className="px-4 py-3 text-xs font-semibold text-gray-700 whitespace-nowrap">₹{parseFloat(inv.amount).toLocaleString('en-IN',{minimumFractionDigits:2})}</td>
                        <td className="px-4 py-3 text-xs whitespace-nowrap">
                          {inv.filePath ? (
                            <a href={'https://api.prodiligix.com' + inv.filePath} target="_blank" rel="noreferrer"
                              className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-white text-xs font-medium w-fit"
                              style={{backgroundColor:BRAND}}>
                              <MdDownload size={14}/> Download
                            </a>
                          ) : <span className="text-gray-400 text-xs">No file</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {activeTab === 'shipment' && (
          <div className="space-y-4">
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
              <p className="text-sm font-semibold text-gray-700 mb-4">Export Shipment Invoices</p>
              <div className="flex flex-wrap gap-4 items-end">
                <div className="flex flex-col gap-1">
                  <label className="text-xs text-gray-500 font-medium">Start Date</label>
                  <input
                    type="date"
                    value={startDate}
                    onChange={e => setStartDate(e.target.value)}
                    className="border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#068BC9]/30 focus:border-[#068BC9]"
                  />
                </div>
                <div className="flex flex-col gap-1">
                  <label className="text-xs text-gray-500 font-medium">End Date</label>
                  <input
                    type="date"
                    value={endDate}
                    onChange={e => setEndDate(e.target.value)}
                    className="border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#068BC9]/30 focus:border-[#068BC9]"
                  />
                </div>
                <button
                  onClick={handleShipmentExport}
                  disabled={exportLoading}
                  className="flex items-center gap-2 px-5 py-2 rounded-lg text-white text-sm font-medium disabled:opacity-60"
                  style={{ backgroundColor: BRAND }}
                >
                  {exportLoading ? (
                    <>
                      <svg className="animate-spin h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
                      </svg>
                      Exporting...
                    </>
                  ) : (
                    <>
                      <MdDownload size={16}/> Download Excel
                    </>
                  )}
                </button>
              </div>
              <p className="text-xs text-gray-400 mt-3">
                Leave dates empty to export all shipment invoices. The Excel file will include invoice number, dates, addresses, items, and declared values.
              </p>
            </div>

            <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-8 text-center">
              <MdLocalShipping size={36} className="text-gray-200 mx-auto mb-2" />
              <p className="text-sm text-gray-400">Select a date range and click Download Excel to export your shipment invoices.</p>
            </div>
          </div>
        )}
      </div>
    </ClientLayout>
  );
}
